import styled from 'styled-components';
import { colors, radius } from '../../../../styles/tokens';

export const Container = styled.div`
  padding: 40px;
  min-height: 100%;

  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 1199px) {
    padding: 20px;
  }
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

export const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: ${colors.onSurface};
  letter-spacing: -0.01em;
  margin-bottom: 4px;
`;

export const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${colors.onSurfaceVariant};
`;

export const BackButton = styled.button`
  align-self: flex-start;

  display: flex;
  align-items: center;
  gap: 6px;

  background: transparent;
  border: none;
  cursor: pointer;

  font-size: 13px;
  font-weight: 600;
  color: ${colors.onSurfaceVariant};

  transition: color 0.2s;

  &:hover {
    color: ${colors.onSurface};
  }
`;

export const Panel = styled.div`
  padding: 24px;
  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainerLow};
`;

export const PanelTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.onSurface};
  margin-bottom: 4px;
`;

export const PanelSubtitle = styled.p`
  font-size: 12px;
  color: ${colors.onSurfaceVariant};
  margin-bottom: 20px;
`;

export const TrialWarning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  padding: 12px 14px;
  margin-bottom: 16px;
  border-radius: ${radius.DEFAULT};
  background-color: ${colors.tertiaryContainer}26;

  font-size: 12px;
  color: ${colors.onSurface};
`;

export const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  padding: 12px 4px;
  border-bottom: 1px solid ${colors.surfaceContainerHigh}80;

  &:last-child {
    border-bottom: none;
  }
`;

export const FeatureRowColumn = styled(FeatureRow)`
  flex-direction: column;
  align-items: stretch;
`;

export const FeatureInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const FeatureName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.onSurface};
`;

export const FeatureMeta = styled.span`
  font-size: 11px;
  color: ${colors.onSurfaceVariant};

  code {
    font-family: monospace;
  }
`;

export const AddonOnlyBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: ${radius.full};

  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.tertiaryContainer};
  background-color: ${colors.tertiaryContainer}26;
`;

export const ValueCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ToggleSwitch = styled.input`
  appearance: none;
  width: 40px;
  height: 22px;
  border-radius: ${radius.full};
  background-color: ${colors.surfaceContainerHigh};
  position: relative;
  cursor: pointer;
  flex-shrink: 0;

  transition: background-color 0.15s;

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: ${colors.onSurfaceVariant};
    transition: transform 0.15s, background-color 0.15s;
  }

  &:checked {
    background-color: ${colors.primary};
  }

  &:checked::before {
    transform: translateX(18px);
    background-color: ${colors.onPrimary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const ValueInput = styled.input`
  width: 80px;
  height: 34px;
  padding: 0 10px;

  background-color: ${colors.surfaceContainer};
  border: none;
  border-radius: ${radius.DEFAULT};

  font-size: 13px;
  color: ${colors.onSurface};

  &:disabled {
    opacity: 0.4;
  }
`;

export const UnlimitedLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;

  font-size: 11px;
  color: ${colors.onSurfaceVariant};
  cursor: pointer;
  white-space: nowrap;
`;

export const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 34px;
  height: 34px;
  border: none;
  border-radius: ${radius.DEFAULT};
  cursor: pointer;

  background-color: ${colors.surfaceContainer};
  color: ${colors.onSurfaceVariant};

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// Card do topo -- separado do Panel das abas de propósito: não muda de
// conteúdo entre Permissões/Pacotes, então vive fora do que alterna.
export const SummaryCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;

  padding: 24px 28px;
  border-radius: ${radius.md};
  border: 1px solid ${colors.surfaceContainerHigh};
  background-color: ${colors.surfaceContainerLow};
`;

export const SummaryHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 560px;
`;

export const SummaryTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const SummaryGymName = styled.h2`
  font-size: 20px;
  font-weight: 800;
  color: ${colors.onSurface};
`;

export const SummaryBadge = styled.span`
  padding: 4px 10px;
  border-radius: ${radius.full};
  border: 1px solid ${colors.tertiaryContainer}80;

  font-size: 11px;
  font-weight: 700;
  color: ${colors.tertiaryContainer};
  white-space: nowrap;
`;

export const SummaryDescription = styled.p`
  font-size: 12.5px;
  line-height: 1.5;
  color: ${colors.onSurfaceVariant};

  strong {
    color: ${colors.onSurface};
  }

  button {
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: ${colors.tertiaryContainer};
    text-decoration: underline;
    cursor: pointer;
  }
`;

export const SummaryEquation = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

export const SummaryOperator = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
`;

export const SummaryBox = styled.div<{ emphasis?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  padding: 12px 18px;
  border-radius: ${radius.DEFAULT};
  border: 1px solid
    ${props => (props.emphasis ? colors.tertiaryContainer : colors.surfaceContainerHigh)};
  background-color: ${props =>
    props.emphasis ? `${colors.tertiaryContainer}14` : colors.surfaceContainer};
`;

export const SummaryBoxLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const SummaryBoxValue = styled.span<{ emphasis?: boolean }>`
  font-size: 18px;
  font-weight: 800;
  color: ${props =>
    props.emphasis ? colors.tertiaryContainer : colors.onSurface};
`;

export const TabRow = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  width: fit-content;
  border-radius: ${radius.DEFAULT};
  background-color: ${colors.surfaceContainer};
`;

export const TabButton = styled.button<{ active: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: ${radius.DEFAULT};
  cursor: pointer;

  font-size: 13px;
  font-weight: 700;

  background-color: ${props =>
    props.active ? colors.surfaceContainerLow : 'transparent'};
  color: ${props =>
    props.active ? colors.onSurface : colors.onSurfaceVariant};
`;

export const GroupSection = styled.div`
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid ${colors.surfaceContainerHigh};

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

export const GroupTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;

  padding: 8px 12px;
  margin: 0 0 10px;
  border-radius: ${radius.DEFAULT};
  border-left: 3px solid ${colors.tertiaryContainer};
  background-color: ${colors.surfaceContainer};

  font-size: 13px;
  font-weight: 800;
  color: ${colors.onSurface};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

export const PlanIncludedBadge = styled.span`
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: ${radius.full};

  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${colors.primary};
  background-color: ${colors.primary}1f;
`;

export const AddAddonForm = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 10px;
  padding: 12px;
  border-radius: ${radius.DEFAULT};
  background-color: ${colors.surfaceContainer};

  & > *:first-child {
    flex: 1;
  }
`;

export const AddAddonLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;

  font-size: 10px;
  color: ${colors.onSurfaceVariant};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const MaskedFieldGroup = styled.div`
  display: flex;
  align-items: stretch;
  border-radius: ${radius.DEFAULT};
  overflow: hidden;
`;

export const MaskedFieldUnit = styled.span`
  display: flex;
  align-items: center;
  padding: 0 8px;
  background-color: ${colors.surfaceContainerHigh};
  font-size: 12px;
  color: ${colors.onSurfaceVariant};
`;

export const MaskedInput = styled.input`
  width: 90px;
  height: 34px;
  padding: 0 10px;

  background-color: ${colors.surfaceContainerHigh};
  border: none;

  font-size: 13px;
  color: ${colors.onSurface};

  &:disabled {
    opacity: 0.4;
  }
`;

export const PackagesTable = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${radius.DEFAULT};
  overflow: hidden;
`;

export const PackagesTableHead = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 0.8fr 0.8fr 1fr 40px;
  gap: 10px;
  padding: 8px 12px;

  font-size: 10px;
  font-weight: 700;
  color: ${colors.onSurfaceVariant};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const PackagesTableRow = styled.div<{ highlight?: boolean }>`
  display: grid;
  grid-template-columns: 1.4fr 0.8fr 0.8fr 1fr 40px;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;

  border-radius: ${radius.DEFAULT};
  background-color: ${props =>
    props.highlight ? `${colors.primary}14` : colors.surfaceContainer};
  margin-bottom: 4px;

  font-size: 12.5px;
  color: ${colors.onSurface};
`;

export const OriginBadge = styled.span<{ plan?: boolean }>`
  display: inline-block;
  width: fit-content;
  padding: 2px 8px;
  border-radius: ${radius.full};

  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => (props.plan ? colors.primary : colors.tertiaryContainer)};
  background-color: ${props =>
    props.plan ? `${colors.primary}1f` : `${colors.tertiaryContainer}26`};
`;

export const EmptyDetail = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;

  border-radius: ${radius.md};
  background-color: ${colors.surfaceContainerLow};

  font-size: 13px;
  color: ${colors.onSurfaceVariant};
`;
